import {useEffect, useMemo, useState} from 'react';
import './task-details-card.scss';
import {convertArrayToIndex} from '../../../utils/functions';
import {ConfigurationsIndex, ConfigurationsValuesIndex} from '../../../types';
import {EntityUserCarePlansRepository} from '../../../repositories/entity-user-care-plans';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {EntityUsersRepository} from '../../../repositories/entity-users';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {ActivitiesGroup} from '../../../backend/interfaces/activities-group';
import {CarePlan} from '../../../backend/interfaces/care-plan';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {CareTaskComplementary} from '../../../backend/interfaces/care-task-complementary';
import {EntityUser} from '../../../backend/responses/entity-user';
import {EntitySocialResponse} from '../../../backend/interfaces/entity-social-response';
import TaskCard from '../task-card/task-card';
import {CarePlanActivitiesGroupType} from '../../../backend/interfaces/care-plan-activities-group-type';
import {ActivityType} from '../../../backend/interfaces/activity-type';

interface TaskDetailsCardProps {
  task?: CareTaskComplementary;
  entityId: string;
  entitySocialResponses?: Array<EntitySocialResponse>;
  onSeniorAction?: (senior: EntityUser) => void;
  seniorActionsOpen?: boolean;
  seniorActionsDisabled?: boolean;
}

const TaskDetailsCard = ({
  task,
  entityId,
  entitySocialResponses,
  onSeniorAction,
  seniorActionsOpen,
  seniorActionsDisabled,
}: TaskDetailsCardProps) => {
  // State
  const [activitiesGroup, setActivitiesGroup] = useState<ActivitiesGroup | undefined>(undefined);
  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex>(undefined);
  const [configurationsValuesIndex, setConfigurationsValuesIndex] =
    useState<ConfigurationsValuesIndex>(undefined);
  const [senior, setSenior] = useState<EntityUser | undefined>(undefined);
  const [taskConfigurations, setTaskConfigurations] = useState<
    Array<CarePlanConfiguration> | undefined
  >(undefined);
  const [taskConfigurationsValuesIndex, setTaskConfigurationsValuesIndex] =
    useState<ConfigurationsValuesIndex>(undefined);
  const [types, setTypes] = useState<ActivityType>();

  const seniorSocialResponses = useMemo(() => {
    if (
      entitySocialResponses &&
      entitySocialResponses?.length > 0 &&
      senior?.affiliation?.socialResponses &&
      senior?.affiliation?.socialResponses?.length > 0
    ) {
      return senior.affiliation.socialResponses.map((socialResponseId) => {
        return entitySocialResponses.find(
          (socialResponse) => socialResponse.id === socialResponseId
        );
      });
    } else {
      return [];
    }
  }, [entitySocialResponses, senior?.affiliation]);

  useEffect(() => {
    if (!task) return;

    Promise.all<
      [
        Promise<CarePlan | null>,
        Promise<ActivitiesGroup | null>,
        Promise<Array<CarePlanConfiguration>>,
        Promise<EntityUser>,
        Promise<Array<CarePlanConfiguration>>,
        Promise<ActivityType | null>,
      ]
    >([
      // Load Careplan
      EntityUserCarePlansRepository.instance.getCarePlan(
        entityId,
        task.careTask.userId,
        task.careTask.activitiesSectorId
      ),
      // Load Activities Group (to obtain the Configuration layout)
      EntityActivitiesRepository.instance.getGroup(
        entityId,
        task.careTask.activitiesSectorId,
        task.careTask.activitiesGroupId
      ),

      // Load Activities Group Configurations (to list the configuration values)
      EntityActivitiesRepository.instance.getTypeCarePlanConfigurations(
        entityId,
        task.careTask.activitiesSectorId,
        task.careTask.activitiesGroupId,
        task.careTask.activityTypeId
      ),

      // Load user info
      EntityUsersRepository.instance.getById(entityId, task.careTask.userId),

      // Load Tasks Group Configurations (to list the configuration values)
      EntityActivitiesRepository.instance.getGroupCarePlanConfigurations(
        entityId,
        task.careTask.activitiesSectorId,
        task.careTask.activitiesGroupId
      ),
      EntityActivitiesRepository.instance.getType(
        entityId,
        task.careTask.activitiesSectorId,
        task.careTask.activitiesGroupId,
        task.careTask.activityTypeId
      ),
    ]).then(
      ([
        carePlan,
        activitiesGroup,
        typePlanConfiguration,
        user,
        groupPlanConfiguration,
        activityTypes,
      ]: [
        CarePlan | null,
        ActivitiesGroup | null,
        Array<CarePlanConfiguration>,
        EntityUser,
        Array<CarePlanConfiguration>,
        ActivityType | null,
      ]) => {
        if (activitiesGroup) {
          const entry = carePlan?.activitiesGroups?.find(
            (it) => it.activitiesGroupId === activitiesGroup.id
          );

          const carePlanActivityType: CarePlanActivitiesGroupType | undefined =
            entry?.activityTypes?.find((it) => it.id === task.careTask.activityTypeEntryId);

          const typeConfigurationValuesIndex =
            carePlanActivityType?.configurations &&
            convertArrayToIndex<CarePlanActivitiesGroupApiRequestConfigurationsItem>(
              carePlanActivityType.configurations,
              'carePlanConfigurationId'
            );

          // Build values index
          const valuesIndex =
            convertArrayToIndex<CarePlanActivitiesGroupApiRequestConfigurationsItem>(
              entry?.configurations ?? [],
              'carePlanConfigurationId'
            );

          // Build Configurations Index
          const configurationsIndex = convertArrayToIndex<CarePlanConfiguration>(
            groupPlanConfiguration,
            'id'
          );
          setConfigurationsIndex(configurationsIndex);

          setSenior(user);

          setTaskConfigurationsValuesIndex(typeConfigurationValuesIndex);

          setActivitiesGroup(activitiesGroup);

          setConfigurationsValuesIndex(valuesIndex);

          setTaskConfigurations(typePlanConfiguration);

          setTypes(activityTypes || undefined);
        } else {
          // Not found
        }
      }
    );
  }, [entityId, task]);

  return (
    <TaskCard
      senior={senior}
      displayType="user"
      avatarBorderColor={seniorSocialResponses[0]?.foregroundColor}
      taskDetails={task}
      activitiesGroup={activitiesGroup}
      configurationsIndex={configurationsIndex}
      configurationsValuesIndex={configurationsValuesIndex}
      taskConfigurationsValuesIndex={taskConfigurationsValuesIndex}
      taskConfigurations={taskConfigurations}
      types={types}
      onSeniorAction={onSeniorAction}
      seniorActionsOpen={seniorActionsOpen}
      seniorActionsDisabled={seniorActionsDisabled}
      defaultExpand
    />
  );
};

export default TaskDetailsCard;
